<template>
  <div class="section content">
    <validation-observer
      ref="form-filing-profile"
      tag="form"
    >
      <div v-if="formTitle || formDesc">
        <div class="section">
          <div class="columns">
            <div class="column has-text-centered">
              <h2 class="title is-2">
                {{ formTitle }}
              </h2>
              <div class="subtitle is-2">
                {{ formDesc }}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div class="columns">
        <div class="column is-4">
          <textbox-vee
            id="first-name"
            v-model="profile.firstName"
            placeholder="First Name"
            :rules="!skipRequirements ? 'required' : ''"
            required
          />
        </div>
        <div class="column is-2">
          <textbox
            id="mid-init"
            v-model="profile.midInit"
            placeholder="MI"
          />
        </div>
        <div class="column is-4">
          <textbox-vee
            id="last-name"
            v-model="profile.lastName"
            placeholder="Last Name"
            :rules="!skipRequirements ? 'required' : ''"
            required
          />
        </div>
        <div class="column is-2">
          <textbox
            id="suffix"
            v-model="profile.suffix"
            placeholder="Suffix"
          />
        </div>
      </div>
      <div class="columns">
        <div class="column is-4">
          <textbox-vee
            id="street-address"
            v-model="profile.homeStreetAddress"
            placeholder="Street Address"
            :rules="!skipRequirements ? 'required' : ''"
            required
          />
        </div>
        <div class="column is-3">
          <textbox-vee
            id="home-city"
            v-model="profile.homeCity"
            placeholder="City"
            :rules="!skipRequirements ? 'required' : ''"
            required
          />
        </div>
        <div class="column is-3">
          <dropdown-vee
            v-model="profile.homeState"
            :options="states"
            placeholder="State"
            name="State"
            :rules="!skipRequirements ? 'required' : ''"
            required
          />
        </div>
        <div class="column is-2">
          <textbox-vee
            id="home-zip"
            v-model="profile.homeZip"
            placeholder="Zip Code"
            :rules="!skipRequirements ? 'required|zipCode' : ''"
            required
          />
        </div>
      </div>
      <div class="columns">
        <div
          v-if="parseInt(profile.homeState) === 220"
          class="column is-4"
        >
          <dropdown-vee
            v-model="profile.countyOfResidence"
            :options="paCounties"
            placeholder="County"
            :rules="!skipRequirements ? 'required' : ''"
            :required="!skipRequirements ? 'required' : undefined"
          />
        </div>
        <div
          v-if="showEmail"
          class="column is-4"
        >
          <textbox-vee
            id="emailAddress"
            v-model="profile.emailAddress"
            :rules="!skipRequirements ? 'required|email' : ''"
            :required="!skipRequirements ? 'required' : undefined"
            placeholder="E-mail"
            autocomplete="off"
          />
        </div>
        <div class="column is-4">
          <textbox-vee
            id="user-home-phone"
            v-model="profile.homePhone"
            placeholder="Home Phone"
            rules="phoneNumber"
            desc="Format ###-###-####"
          />
        </div>
      </div>
      <div class="section">
        <div class="columns">
          <div class="column has-text-centered">
            <h2 class="title is-2">
              City Official / Employee?
            </h2>
            <div class="subtitle is-2">
              In the past year have you served as: a City employee, an elected City official, a member of the City of the Philadelphia Board of Revision of Taxes (BRT), OR the Chair of the Commission on Human Relations of the City of Philadelphia.
            </div>
          </div>
        </div>
        <div class="columns is-centered">
          <div class="column is-4">
            <div class="columns is-multiline">
              <div class="column is-half has-text-centered">
                <radio-adv
                  id="is-city-employee-check-true"
                  v-model="profile.isCityEmployee"
                  :value="true"
                >
                  Yes
                </radio-adv>
              </div>
              <div class="column is-half has-text-centered">
                <radio-adv
                  id="is-city-employee-check-false"
                  v-model="profile.isCityEmployee"
                  :value="false"
                >
                  No
                </radio-adv>
              </div>
            </div>
          </div>
        </div>
      </div>
    </validation-observer>
  </div>
</template>
<script>
import { controlValueHelpers } from '@/mixins/controlValue';
import { filerScreenHelpers } from '@/mixins/filerScreen';
import { Textbox, Dropdown } from '@phila/phila-ui';

import { withValidation } from 'vee-validate';
import RadioAdv from '@/components/phila-ui/RadioAdv';

const TextboxVee = withValidation(Textbox);
const DropdownVee = withValidation(Dropdown);

export default {
  name: 'Profile',
  components: {
    Textbox,
    TextboxVee,
    DropdownVee,
    RadioAdv,
  },
  mixins: [
    filerScreenHelpers,
    controlValueHelpers,
  ],
  props: {
    type: {
      type: String,
      default: 'new-user',
    },
    formTitle: {
      type: String,
      default: '',
    },
    formDesc: {
      type: String,
      default: '',
    },
    showEmail: {
      type: Boolean,
      default: false,
    },
    skipRequirements: {
      type: Boolean,
      default: false,
    },
  },
  data() {
    return {
      profile: {},
    };
  },
  computed: {
    states () {
      return this.controlValueDropdown('state');
    },
    paCounties () {
      return this.controlValueDropdown('county');
    },
  },
  watch: {
    profile: {
      async handler () {
        this.$emit('profile', this.profile);
      },
      deep: true,
    },
  },
  async created () {
    await this.initialFetch();
    await this.initialAssign();
    this.$emit('profileReady', true);
  },
  methods: {
    async initialAssign () {
      if (this.type === 'user') {
        this.profile = await this.getUserProfile();
      } else if (this.type === 'tempUser') {
        this.profile = await this.getTempUserProfile();
      } else if (this.type === 'filing') {
        this.profile = await this.getCurrentFiling();
      }
    },
    async initialFetch () {
      let promises = [];
      promises.push(this.$store.dispatch('admin/getControlValue', 'state'));
      promises.push(this.$store.dispatch('admin/getControlValue', 'county'));

      if (this.type === 'user') {
        promises.push(this.$store.dispatch('user/fetchUserProfile'));
      } else if (this.type === 'tempUser') {
        promises.push(this.$store.dispatch('user/fetchUserProfile', this.$store.state.user.tempId));
      } else if (this.type === 'filing') {
        //something
      }
      await Promise.all(promises);
    },
    getUserProfile () {
      return this.cloneObj(this.$store.state.user.profile);
    },
    getTempUserProfile () {
      return this.cloneObj(this.$store.state.user.tempProfile);
    },
  },
};
</script>
